/**
 * Inquiry form inline success
 *
 * Shopify protects all storefront pages with `X-Frame-Options: DENY` so we
 * can't post the form to a hidden iframe. And POSTing via fetch breaks the
 * site's invisible captcha (the captcha JS only populates the token on a
 * native submit event). So the only reliable strategy is:
 *
 *   1. Let the native submit go through — Shopify validates captcha,
 *      processes, sends email, and 302-redirects to the referring page
 *      with `?contact_posted=true&form_type=contact#contact_form`.
 *   2. On page load, look for that flag. When present, mount the on-brand
 *      success state inline in the matching container (and re-open the
 *      contact modal if the form lives inside one).
 *   3. Stash the submitter's email in sessionStorage so we can echo it
 *      back on the post-reload screen.
 *
 * This means the page does reload once, but it's a single reload — the URL
 * is then cleaned up via history.replaceState and the modal stays "open"
 * with the success state from the user's perspective.
 *
 * Expected DOM:
 *   <div data-inquiry-form-container>
 *     <div data-inquiry-success-region role="status" aria-live="polite" aria-atomic="true"></div>
 *     <form action="/contact" method="post"> ... </form>
 *     <template data-inquiry-success-template>
 *       <!-- {% render 'inquiry-success', mode: 'modal' %} -->
 *     </template>
 *   </div>
 *
 * Dispatches on the form:
 *   inquiry-form:submit-start
 *   inquiry-form:success        (detail: { email })
 *   inquiry-form:reset
 */

(() => {
  const SUCCESS_QUERY_FLAG = 'contact_posted';
  const CONTACT_ACTION_RE = /\/contact(\?|#|$)/;
  const STORAGE_EMAIL_KEY = 'inquiry-form-email';
  const STORAGE_SOURCE_KEY = 'inquiry-form-source';

  function findForm(container) {
    return container ? container.querySelector('form') : null;
  }

  function getEmail(form) {
    const input = form.querySelector('input[type="email"], input[name="contact[email]"]');
    return input ? input.value.trim() : '';
  }

  function setSubmitBusy(form, busy) {
    const submit = form.querySelector('[type="submit"]');
    if (!submit) return;
    if (busy) {
      submit.dataset.originalLabel = submit.textContent;
      submit.setAttribute('aria-busy', 'true');
      submit.disabled = true;
      submit.textContent = submit.dataset.busyLabel || 'Sending…';
    } else {
      submit.removeAttribute('aria-busy');
      submit.disabled = false;
      if (submit.dataset.originalLabel) submit.textContent = submit.dataset.originalLabel;
    }
  }

  function clearErrors(container) {
    container.querySelectorAll('[data-inquiry-form-errors]').forEach((el) => el.remove());
  }

  function showErrors(container, form, messages) {
    clearErrors(container);
    if (!messages.length) return;
    const alert = document.createElement('div');
    alert.className = 'alert errors';
    alert.setAttribute('role', 'alert');
    alert.setAttribute('data-inquiry-form-errors', '');
    const heading = document.createElement('div');
    heading.setAttribute('role', 'heading');
    heading.setAttribute('aria-level', '3');
    heading.textContent = 'Please adjust the following:';
    alert.appendChild(heading);
    const list = document.createElement('ul');
    list.className = 'styled-list';
    messages.forEach((msg) => {
      const li = document.createElement('li');
      li.textContent = msg;
      list.appendChild(li);
    });
    alert.appendChild(list);
    form.insertAdjacentElement('beforebegin', alert);
    const firstField = form.querySelector('input:not([type="hidden"]), textarea, select');
    firstField?.focus();
  }

  function mountSuccess(container, form, email) {
    const template = container.querySelector('[data-inquiry-success-template]');
    const region = container.querySelector('[data-inquiry-success-region]');
    if (!template || !region) return null;

    // If the success region was already populated (e.g. by another mount or
    // by a server-rendered success snippet), don't double-mount.
    if (region.children.length > 0) return region.querySelector('[data-inquiry-success]');

    const clone = template.content.cloneNode(true);
    region.innerHTML = '';
    region.appendChild(clone);

    const success = region.querySelector('[data-inquiry-success]');
    const emailLine = region.querySelector('[data-inquiry-success-email]');
    const emailValue = region.querySelector('[data-inquiry-success-email-value]');
    if (email && emailLine) {
      emailLine.hidden = false;
      if (emailValue) {
        emailValue.textContent = email;
      } else {
        emailLine.textContent = emailLine.textContent.replace(/\s*$/, ' ') + email;
      }
    }

    if (form) form.hidden = true;
    clearErrors(container);

    if (success) {
      window.setTimeout(() => success.focus(), 50);
    }

    return success;
  }

  function resetForm(container, form) {
    const region = container.querySelector('[data-inquiry-success-region]');
    if (region) region.innerHTML = '';
    form.reset();
    form.hidden = false;
    clearErrors(container);
    form.dispatchEvent(new CustomEvent('inquiry-form:reset', { bubbles: true }));
    const firstField = form.querySelector('input:not([type="hidden"]), textarea, select');
    firstField?.focus();
  }

  /**
   * Capture the email on submit so we can echo it back after Shopify
   * redirects us to ?contact_posted=true. We deliberately don't preventDefault
   * — the native submit drives Shopify's captcha + validation flow.
   */
  function handleSubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const container = form.closest('[data-inquiry-form-container]');
    if (!container) return;

    const action = form.getAttribute('action') || '';
    if (!CONTACT_ACTION_RE.test(action)) return;

    try {
      const email = getEmail(form);
      if (email) {
        sessionStorage.setItem(STORAGE_EMAIL_KEY, email);
      }
      // Note where the form lived so we can re-open the right modal post-reload.
      let source = 'page';
      if (container.closest('[data-contact-modal]')) source = 'global-modal';
      else if (container.closest('.catalog-contact-modal')) source = 'catalog-modal';
      sessionStorage.setItem(STORAGE_SOURCE_KEY, source);
    } catch {
      // Storage may be blocked (private mode etc.) — proceed without email echo.
    }

    setSubmitBusy(form, true);
    form.dispatchEvent(new CustomEvent('inquiry-form:submit-start', { bubbles: true }));
    // Native submit proceeds: Shopify will redirect with the contact_posted flag.
  }

  /**
   * On any page load, if we landed here from a successful contact-form POST,
   * mount the success state inline (and reopen the relevant modal) so the
   * user sees on-brand confirmation instead of a stray query string.
   */
  function checkSubmittedSuccess() {
    const params = new URLSearchParams(window.location.search);
    if (params.get(SUCCESS_QUERY_FLAG) !== 'true') return;

    let savedEmail = '';
    let source = '';
    try {
      savedEmail = sessionStorage.getItem(STORAGE_EMAIL_KEY) || '';
      source = sessionStorage.getItem(STORAGE_SOURCE_KEY) || '';
      sessionStorage.removeItem(STORAGE_EMAIL_KEY);
      sessionStorage.removeItem(STORAGE_SOURCE_KEY);
    } catch {}

    const containers = document.querySelectorAll('[data-inquiry-form-container]');
    let mountedAnywhere = false;
    let mountedInGlobalModal = false;
    let mountedInCatalogModal = false;

    containers.forEach((container) => {
      const form = findForm(container);
      if (!form) return;

      // Skip containers that have already had success rendered server-side
      // (the {% if form.posted_successfully? %} branch on contact pages).
      const formAlreadyHasSuccess = form.querySelector('[data-inquiry-success]');
      if (formAlreadyHasSuccess) {
        // Mirror the server-rendered success into the live region so aria-live
        // announces it, but leave the form's contents alone.
        mountedAnywhere = true;
        return;
      }

      mountSuccess(container, form, savedEmail);
      mountedAnywhere = true;

      if (container.closest('[data-contact-modal]')) mountedInGlobalModal = true;
      if (container.closest('.catalog-contact-modal')) mountedInCatalogModal = true;
    });

    // Re-open the modal the user was looking at when they submitted.
    if (mountedInGlobalModal && (source === 'global-modal' || !source)) {
      window.setTimeout(() => {
        document.dispatchEvent(new CustomEvent('contact-modal:request-open', {
          detail: { preserveSuccess: true }
        }));
      }, 50);
    } else if (mountedInCatalogModal && source === 'catalog-modal') {
      window.setTimeout(() => {
        document.dispatchEvent(new CustomEvent('catalog-contact-modal:request-open', {
          detail: { preserveSuccess: true }
        }));
      }, 50);
    }

    // Strip the query flag + form_type + form_key + #contact_form hash so
    // a refresh doesn't re-trigger this and the URL bar stays clean.
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete(SUCCESS_QUERY_FLAG);
      url.searchParams.delete('form_type');
      url.searchParams.delete('form_key');
      const cleanHash = url.hash === '#contact_form' ? '' : url.hash;
      const cleanUrl = url.pathname + (url.search ? url.search : '') + cleanHash;
      window.history.replaceState({}, '', cleanUrl);
    } catch {}

    return mountedAnywhere;
  }

  function handleSuccessClick(event) {
    const resetBtn = event.target.closest('[data-inquiry-success-reset]');
    const closeBtn = event.target.closest('[data-inquiry-success-close]');
    if (!resetBtn && !closeBtn) return;

    const container = (resetBtn || closeBtn).closest('[data-inquiry-form-container]');
    if (!container) return;

    if (closeBtn) {
      closeBtn.dispatchEvent(new CustomEvent('inquiry-form:close-request', { bubbles: true }));
      return;
    }

    const form = findForm(container);
    if (!form) {
      window.location.href = window.location.pathname;
      return;
    }

    if (form.hidden) {
      resetForm(container, form);
    } else {
      window.location.href = window.location.pathname;
    }
  }

  /**
   * Preview mode for QA — inspect each state without real submissions.
   *
   *   ?inquiry_preview=success
   *   ?inquiry_preview=success&inquiry_email=you@example.com
   *   ?inquiry_preview=error
   *   ?inquiry_preview=busy
   */
  function applyPreviewMode() {
    const params = new URLSearchParams(window.location.search);
    const mode = params.get('inquiry_preview');
    if (!mode) return;

    const email = params.get('inquiry_email') || '';
    const containers = document.querySelectorAll('[data-inquiry-form-container]');

    containers.forEach((container) => {
      const form = findForm(container);
      if (!form) return;

      if (mode === 'success') {
        mountSuccess(container, form, email);
      } else if (mode === 'error') {
        showErrors(container, form, [
          "Email can't be blank",
          "Name can't be blank"
        ]);
      } else if (mode === 'busy') {
        setSubmitBusy(form, true);
      }
    });

    if (mode === 'success' || mode === 'error' || mode === 'busy') {
      console.info('[inquiry-preview] Active mode:', mode, email ? `(email: ${email})` : '');
    }
  }

  function init() {
    // Capture submit so we record context before Shopify's captcha JS
    // takes over the event flow.
    document.addEventListener('submit', handleSubmit, true);
    document.addEventListener('click', handleSuccessClick);
    checkSubmittedSuccess();
    applyPreviewMode();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
